import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';

import { BehaviorSubject, of, Subject, filter, combineLatestWith, map, first } from 'rxjs';
import { control, Control, FullscreenOptions, latLng, Map, MapOptions, ZoomAnimEvent } from 'leaflet';
import 'src/app/modules/shared/components/map/controls/view-entire-trip-control/view-entire-trip-control.js';
import 'src/app/modules/shared/components/map/controls/follow-asset-control/follow-asset-control.js';
import 'src/app/modules/shared/components/map/controls/timeline-control/timeline-control.js';
import { LeafletService } from '@app/modules/location/services/leaflet.service';
import {
  LeafletConfigService,
  OverlayCheckboxes
} from '@app/modules/location/services/leaflet-layer-configs/leaflet-config.service';
// we need this polyfill for Safari < v13 or else we get reference error for ResizeObserver
import { ResizeObserver } from 'resize-observer';
import { SettingsApiService } from '@app/services/settings-api.service';
import { distinctUntilKeyChanged, switchMap, take, takeUntil } from 'rxjs/operators';
import { MapSettings, defaultMapCoordinates, SettingInfo } from '@app/modules/location/models/settings.model';
import { ResourceLoadState } from '@app/store/filters/models/resource-load.state';
import { PlatformFacade } from '@app/modules/platform/facade/platform.facade';
import { FeatureToggleService } from '@app/modules/feature-toggles/services/feature-toggle.service';
import { environment as env } from '@environments/environment';
import { LeafletZoneService } from '@app/modules/zones/services/leaflet-zone.service';
import { ZoneCheckboxService } from '@app/modules/zones/services/zone-checkbox.service';
import { EventTrackingFacade } from '@app/modules/location/facade/event-tracking.facade';
import { PermissionsService } from '@app/modules/auth';

declare module 'leaflet' {
  interface Control {
    _addTo(map: Map): Control;
  }

  interface Map {
    _leaflet_id: number;
    _container: HTMLElement;
  }
}

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class MapComponent implements OnDestroy, OnInit {
  constructor(
    public leafletService: LeafletService,
    private leafletConfig: LeafletConfigService,
    public platformFacade: PlatformFacade,
    private settingsService: SettingsApiService,
    private featureToggleService: FeatureToggleService,
    private leafletZoneService: LeafletZoneService,
    private zoneCheckboxService: ZoneCheckboxService,
    private eventTracking: EventTrackingFacade,
    private permsService: PermissionsService
  ) {
    this.platformFacade
      .getIsMobile()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(flag => (this.isMobile = flag === true ? true : false));
    this.featureToggleService
      .isFeatureEnabled('maps-incident-button-enabled')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(featureFlag => {
        this.incidentButtonEnabled = featureFlag === true ? true : false;
        this.showIncidentButton.next(this.incidentButtonEnabled && !this.isMobile);
      });
  }

  @ViewChild('mapEl', { static: true }) public mapElement: ElementRef;

  @Output() map$: EventEmitter<Map> = new EventEmitter();
  @Output() zoom$: EventEmitter<number> = new EventEmitter();
  @Input() mapId = 'map-container';

  public map: Map;
  public zoom: number;
  private onDestroy$ = new Subject();
  public locateControlOptions = new BehaviorSubject<Control.LocateOptions>(
    this.leafletConfig.defaultLocateControlOptions
  );
  public fullscreenControlOptions = new BehaviorSubject<FullscreenOptions>(
    this.leafletConfig.defaultFullscreenControlOptions
  );
  public settingsLoadState = new BehaviorSubject<ResourceLoadState>(ResourceLoadState.INITIAL);
  public loadSuccessful = ResourceLoadState.LOAD_SUCCESSFUL;
  public region = env.region;
  public newOptions: MapOptions = this.leafletConfig.defaultMapOptions;
  public isMobile = false;
  public incidentButtonEnabled = true;
  public showIncidentButton = new BehaviorSubject<boolean>(false);
  private mapResizeObserver = new ResizeObserver(() => {
    this.map?.invalidateSize();
  });

  public isInitialLoad = true;

  ngOnInit() {
    this.leafletConfig
      .getTranslatedControlOptions$('locate')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(options => {
        this.locateControlOptions.next(options);
      });

    this.leafletConfig
      .getTranslatedControlOptions$('fullscreen')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(options => {
        this.fullscreenControlOptions.next(options);
      });

    this.settingsLoadState.next(ResourceLoadState.LOADING);
    this.settingsService
      .getAllSettings()
      .pipe(
        switchMap(settingsMap => {
          if (settingsMap.has(MapSettings.MAP_LAYER)) {
            const layerSetting = settingsMap.get(MapSettings.MAP_LAYER);
            Object.keys(this.leafletConfig.tileLayers).includes(layerSetting.value)
              ? (this.newOptions.layers = [this.leafletConfig.tileLayers[layerSetting.value]])
              : (this.newOptions.layers = [this.leafletConfig.tileLayers.NORMAL_DAY]);
          }
          return of(settingsMap);
        })
      )
      .subscribe(settingsMap => {
        this.settingsLoadState.next(ResourceLoadState.LOAD_SUCCESSFUL);
      });
  }

  ngOnDestroy(): void {
    this.map?.clearAllEventListeners();
    this.map?.remove();

    this.mapResizeObserver.disconnect();

    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  onMapReady(map: Map) {
    this.map = map;
    this.map$.emit(map);
    this.zoom = map.getZoom();
    this.zoom$.emit(this.zoom);
    this.map.setView(latLng(defaultMapCoordinates[this.region].lat, defaultMapCoordinates[this.region].long));

    // add attribution to map
    this.leafletConfig
      .getTranslatedControlOptions$('attribution')
      .pipe(take(1))
      .subscribe(translated => {
        control.attribution().addAttribution(translated).addTo(this.map);
      });

    // remove the attribution prefix (leaflet and flag links)
    this.map.attributionControl?.setPrefix(false);

    // add scale control to map
    control.scale(this.leafletConfig.getLocalizedScaleOptions()).addTo(this.map);

    // add zoom control to map
    this.leafletConfig
      .getTranslatedControlOptions$('zoom')
      .pipe(take(1))
      .subscribe(options => {
        control.zoom(options).addTo(this.map);
      });

    const mapContainer = document.getElementById(this.mapId);
    if (mapContainer) {
      this.mapResizeObserver.observe(mapContainer);
    }

    this.featureToggleService
      .isFeatureEnabled('zones-enabled')
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe(zonesEnabled => {
        if (zonesEnabled) {
          this.onLayersMenuReady();
          return;
        }
      });
  }

  onMapZoomEnd(e: ZoomAnimEvent) {
    this.zoom = e.target.getZoom();
    this.zoom$.emit(this.zoom);
  }

  onLayersMenuReady() {
    this.zoneCheckboxService.isLayersMenuLoaded$
      .pipe(
        filter(isLayersMenuLoaded => isLayersMenuLoaded),
        first(),
        combineLatestWith(
          this.settingsService.userSettingsCache$.pipe(
            map(userSettings => {
              return userSettings.get(MapSettings.MAP_ZONES) as SettingInfo;
            }),
            distinctUntilKeyChanged('value')
          ),
          this.permsService.users$
        ),
        takeUntil(this.onDestroy$)
      )
      .subscribe(([_, zonesSetting, users]) => {
        const userEmail = users[0].email;

        /*

      This is a hack to get the Show Zones checkbox status to carry over when a Zonar/multicompany switches companies for completing https://zonarsystems.atlassian.net/browse/ZTT-3020. Even though the original pattern for adding layers checkboxes was followed to be the same as "Cluster Assets" and "Road Incidents" checkboxes, the changes were not carrying over on company switch, causing buggy inconsistencies and unexpected/inaccurate results. This is probably due to the fact that the zones layer relies on a company context switch in order to change their zones, whereas the cluster assets and road incidents do not have the same dependnecy and can be more simply implemented. 

      
      Much attempt was made to figure out the issue in an elegant manner, but to no avail, so this admittedly regrettable hack is here.

      This whole code block is essentially saying 
      1. We have loaded the page either through an initial page load or a company switch
      2. We look at user settings and see if the Show Zones value from settings is true or false
      4. If the user setting on page load/company switch is true, check Show Zones box using vanilla JS
      5. If the user setting on page load/company switch is false, uncheck the Show Zones box using vanilla JS
      6. When we check/uncheck a box again after the first load, we should be saving that user setting so this.settingsService.userSettingsCache$ can listen for that change and reapply this entire process

      */
        const shouldZonesBeChecked = zonesSetting?.value === 'true';

        const layersMenu = document.querySelector('.leaflet-control-layers.leaflet-control');

        // we need layers menu to be visible on page load in order to get the zones checkbox and adding this class makes it visible
        layersMenu.classList.add('leaflet-control-layers-expanded');

        // doing this resolves a regression where the base layer tile pics for classic, satellite map views were not showing up
        layersMenu.addEventListener('mouseover', _ => {
          this.leafletConfig.addLayerCssClasses();
        });

        // now that zones checkbox is visible, we grab it
        const zoneCheckbox = this.zoneCheckboxService.getZoneCheckboxElement();

        // based off of shouldZonesBeChecked value taken from user settings, we start the page with the box checked/unchecked accordingly
        this.zoneCheckboxService.setZoneCheckboxStatus(shouldZonesBeChecked, zoneCheckbox);

        // When manually setting the checkbox, the leaflet services's checkbox handler won't initially work on the first click, i.e. will not update the settings state. in order for the settings state to be updated, we also save the clicked/unclicked status to settings by listening directly on the DOM element's event listener
        this.zoneCheckboxService.saveZoneCheckboxStatusToSettingsOnClick(zoneCheckbox);

        if (shouldZonesBeChecked) {
          this.handleCheckboxChecked();
          this.eventTracking.trackEvent('zone-checkbox-checked', { userEmail });
        } else {
          this.handleCheckboxCleared();
          this.eventTracking.trackEvent('zone-checkbox-unchecked', { userEmail });
        }

        // after all above is done, remove the leaflet-control-layers-expanded class so the menu is hidden again
        // the isInitialLoad condition prevents a regression where the layers menu would disappear on a user's checkmark click
        if (this.isInitialLoad) {
          layersMenu.classList.remove('leaflet-control-layers-expanded');
        }
        this.isInitialLoad = false;
      });
  }

  handleCheckboxChecked() {
    this.leafletZoneService.addZonesToMap(this.map);
    this.leafletService.handleCheckboxChecked(OverlayCheckboxes.ZONES);
  }

  handleCheckboxCleared() {
    this.leafletZoneService.zoneFeatureGroup.clearLayers();
    this.leafletService.handleCheckboxCleared(OverlayCheckboxes.ZONES);

    // unsubscribe or else we'll get blasted with multiple zones calls under the hood
    this.leafletZoneService?.addZonesSubscription?.unsubscribe();
  }
}
